import {isMobile} from '../../../util/panelbankFlow';
import {DEFAULT_PANEL_SIZE} from '../../../util/panelbankTypes';
import {type PanelBankFlowSectionConfig} from '../types';
import {
  getFlowConfigMaxRowsPerPage,
  getFlowConfigPageSizeOptions,
  MAX_SECTION_PAGE_SIZE,
} from './panelBankFlowPaginationUtils';
import {DEFAULT_SECTION_GAP} from './utils';

/**
 * If user is on a larger screen, we allow them to have multiple columns.
 * We ensure that everything snaps to the closest number of columns so panels are of equal width in a row.
 *
 * Mobile layout differs from desktop in key ways:
 * - Always uses single column layout (1 panel per row) for optimal mobile viewing
 * - Uses mobile-specific pagination settings (mobileRowsPerPage)
 * - Uses mobile-specific columns per page (mobileColumnsPerPage)
 */
export const getFlowLayout = (
  panelBankWidth: number,
  flowConfig: PanelBankFlowSectionConfig
) => {
  const {panelWidth, panelHeight} = getPanelDimensions({
    panelBankWidth,
    flowConfig,
  });
  const rowsPerPage = getRowsPerPage(flowConfig);
  const columnsPerPage = getColumnsPerPage(flowConfig);

  return {
    columnsPerPage,
    panelHeight,
    panelWidth,
    rowsPerPage,
  };
};

const getPanelDimensions = ({
  panelBankWidth,
  flowConfig,
}: {
  panelBankWidth: number;
  flowConfig: PanelBankFlowSectionConfig;
}) => {
  if (isMobile()) {
    return {
      panelWidth: panelBankWidth,
      panelHeight: Math.max(flowConfig.boxHeight, DEFAULT_PANEL_SIZE),
    };
  }

  if (flowConfig.snapToColumns) {
    const columnCount = flowConfig.columnsPerPage;
    const columnWidth = getWidthOfColumn({
      columnCount,
      maxWidth: panelBankWidth,
    });

    return {
      panelWidth: columnWidth,
      panelHeight: flowConfig.boxHeight,
    };
  }

  return {
    panelWidth: flowConfig.boxWidth,
    panelHeight: flowConfig.boxHeight,
  };
};

const getColumnsPerPage = (flowConfig: PanelBankFlowSectionConfig) => {
  if (isMobile()) {
    return flowConfig.mobileColumnsPerPage;
  }
  return flowConfig.columnsPerPage;
};

const getRowsPerPage = (flowConfig: PanelBankFlowSectionConfig) => {
  if (isMobile()) {
    return flowConfig.mobileRowsPerPage;
  }
  return flowConfig.rowsPerPage;
};

export const getMaxNumSectionPages = ({
  columnsPerPage,
  numTotalPanelsInSection,
  rowsPerPage,
}: {
  columnsPerPage: number;
  numTotalPanelsInSection: number;
  rowsPerPage: number;
}) => {
  const panelsPerPage = getPanelsPerPage({columnsPerPage, rowsPerPage});
  return Math.max(0, Math.ceil(numTotalPanelsInSection / panelsPerPage) - 1);
};

/**
 * Mobile pagination differs from desktop: mobile is fixed at 1 column, so pagination
 * controls panels per page directly. Desktop allows multiple columns, so pagination
 * controls rows per page (panels per page = rows × columns).
 */
export const getPanelsPerPage = ({
  columnsPerPage,
  rowsPerPage,
}: {
  columnsPerPage: number;
  rowsPerPage: number;
}) => {
  if (isMobile()) {
    return rowsPerPage;
  }
  return columnsPerPage * rowsPerPage;
};

/**
 * This returns the maximum number of rows that can be displayed in a section
 * based on the number of total panels in a section and the number of panels per row.
 *
 * This is used to ensure the section height is bounded by pagination constraints.
 *
 * @param panelsPerRow - The number of panels per row
 * @returns The maximum number of rows that can be displayed in a section
 */
export const getMaxRowsInSection = ({
  numTotalPanelsInSection,
  columnsPerPage,
}: {
  numTotalPanelsInSection: number;
  columnsPerPage: number;
}) => {
  return Math.min(
    Math.ceil(numTotalPanelsInSection / columnsPerPage),
    Math.ceil(MAX_SECTION_PAGE_SIZE / columnsPerPage)
  );
};

/**
 * This returns the number of rows that can be displayed in a section
 * based on the section height and the panel height.
 * This is useful when user is resizing a section and we
 * want to limit how far they can drag the section.
 */
export const getRowsPerPageWithHeight = ({
  maxRowsInSection,
  panelHeight,
  sectionHeight,
}: {
  /**
   * Number of possible rows a section can have by taking into account
   * the number of total panels that are in a section.
   */
  maxRowsInSection: number;
  panelHeight: number;
  sectionHeight: number;
}) => {
  const rowsPerPage = Math.max(
    1,
    Math.min(
      maxRowsInSection,
      Math.floor((sectionHeight - DEFAULT_SECTION_GAP) / panelHeight)
    )
  );

  return rowsPerPage;
};

/**
 * This returns the new rows per page after user finishes resizing a panel.
 * It needs to consider the section pagination options.
 */
export const getSnappedRowsPerPage = ({
  numVisiblePanels,
  columnsPerPage,
  numTotalPanelsInSection,
}: {
  numVisiblePanels: number;
  columnsPerPage: number;
  numTotalPanelsInSection: number;
}) => {
  // We want to find the closest page size option to the current number of panels
  // so the page size option is selected correctly.
  const pageSizeOptions = getFlowConfigPageSizeOptions({
    columnsPerPage,
    numTotalPanelsInSection,
  });

  // Find the closest page size option to the current number of panels
  let closestOption: number | 'all' = pageSizeOptions[0];
  let minDifference = Infinity;

  for (const option of pageSizeOptions) {
    const optionValue = option === 'all' ? numTotalPanelsInSection : option;
    const difference = Math.abs(optionValue - numVisiblePanels);

    if (difference < minDifference) {
      minDifference = difference;
      closestOption = option;
    }
  }

  // Calculate rows per page based on the closest page size option
  const rowsPerPage = getFlowConfigMaxRowsPerPage({
    columnsPerPage,
    numTotalPanelsInSection,
    option: closestOption,
  });

  return rowsPerPage;
};

/**
 * This returns the height of a section based on the number of panels in the section
 * and the panel height.
 */
export const getSectionHeight = ({
  columnsPerPage,
  panelHeight,
  rowsPerPage,
  numPanelsOnPage,
}: {
  columnsPerPage: number;
  panelHeight: number;
  rowsPerPage: number;
  numPanelsOnPage: number;
}) => {
  // The actual number of rows we want to display dynamically changes based on which page
  // and how many panels are on that page.
  const actualRows = Math.ceil(numPanelsOnPage / columnsPerPage);
  const rows = Math.min(rowsPerPage, actualRows);

  return rows * panelHeight + (rows - 1) * DEFAULT_SECTION_GAP;
};

/**
 * This returns the maximum width of a section based on the number of columns
 * and the panel width.
 */
export const getMaxSectionWidth = ({
  columnsPerPage,
  panelWidth,
}: {
  columnsPerPage: number;
  panelWidth: number;
}) => {
  if (columnsPerPage === 0) {
    console.error(
      'getMaxSectionWidth called with 0 columns. This should never happen.'
    );
    return 0;
  }

  return (
    columnsPerPage * panelWidth + (columnsPerPage - 1) * DEFAULT_SECTION_GAP
  );
};

/**
 * This returns the width of a column based on the number of columns
 * and the maximum width of the section.
 */
export const getWidthOfColumn = ({
  columnCount,
  maxWidth,
}: {
  columnCount: number;
  maxWidth: number;
}) => {
  if (columnCount === 0) {
    console.error(
      'getWidthOfColumn called with 0 columns. This should never happen.'
    );
    return 0;
  }

  return Math.floor(
    (maxWidth - (columnCount - 1) * DEFAULT_SECTION_GAP) / columnCount
  );
};

const WORKSPACE_SNAP_COLUMN_COUNTS = [1, 2, 3, 4, 5, 6, 7, 8];

/**
 * Given a px width, returns the closest number of columns to accomodate that width.
 * (Enables 'snap to columns' behavior when resizing panels.)
 */
export const getSnappedColumn = ({
  currPanelWidth,
  maxWidth,
}: {
  currPanelWidth: number;
  maxWidth: number;
}) => {
  let closestIndex = 0;

  for (let i = 0; i < WORKSPACE_SNAP_COLUMN_COUNTS.length; i++) {
    const snapColumnWidth = getWidthOfColumn({
      columnCount: WORKSPACE_SNAP_COLUMN_COUNTS[i],
      maxWidth,
    });

    const closestWidth = getWidthOfColumn({
      columnCount: WORKSPACE_SNAP_COLUMN_COUNTS[closestIndex],
      maxWidth,
    });

    if (
      Math.abs(currPanelWidth - snapColumnWidth) <
      Math.abs(currPanelWidth - closestWidth)
    ) {
      closestIndex = i;
    }
  }

  return {
    columnCount: WORKSPACE_SNAP_COLUMN_COUNTS[closestIndex],
    width: getWidthOfColumn({
      columnCount: WORKSPACE_SNAP_COLUMN_COUNTS[closestIndex],
      maxWidth,
    }),
  };
};

/**
 * Calculates the CSS transform for positioning a panel in a grid layout
 * using hardware-accelerated translate3d transforms.
 *
 * @param index - The panel's position in the array (0-based)
 * @returns CSS transform string for positioning the panel
 */
export const getPanelTransform = ({
  index,
  columnsPerPage,
  panelWidth,
  panelHeight,
}: {
  index: number;
  columnsPerPage: number;
  panelWidth: number;
  panelHeight: number;
}): string => {
  if (columnsPerPage === 0) {
    console.error(
      'getPanelTransform called with 0 columns. This should never happen.'
    );
    return 'translate3d(0px, 0px, 0)';
  }

  const row = Math.floor(index / columnsPerPage);
  const col = index % columnsPerPage;
  const x = col * (panelWidth + DEFAULT_SECTION_GAP);
  const y = row * (panelHeight + DEFAULT_SECTION_GAP);

  // Intentionally using translate3d to take advantage of GPU acceleration
  return `translate3d(${x}px, ${y}px, 0)`;
};
